import React, { useRef, useEffect } from "react";
import { useController } from "react-hook-form";
import { GdkMultipleSelectBox } from "@gdk/multiple-select-box";
import { classes } from "../../util/style-utils";
import $ from 'jquery';
import { v4 } from "uuid";

/**
 * Gets the selected values from a GdkMultipleSelectBox by its DOM ID.
 * @param {string} id - The DOM ID of the select element
 * @returns {string[]|null} Array of selected option IDs, or null if retrieval fails
 */
function getMultiSelectValues(id) {
    try {
        return $(`#${id}.select2-hidden-accessible`)?.select2("data")?.map(d => d.id);
    } catch {
        return null;
    }
}

/**
 * A React component that renders a multi-select dropdown using GDK MultipleSelectBox.
 * Integrates with react-hook-form for form validation and state management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.control - React Hook Form control object for form state management
 * @param {string} props.name - The name attribute for the form field, used for form registration
 * @param {string} props.label - The display label for the select box
 * @param {Array<{value: string, name: string}>} [props.options=[]] - Array of option objects with value and name properties
 * @param {boolean} [props.disabled=false] - Whether the select box is disabled
 * @param {number} [props.maximumSelectionLength=0] - Maximum number of selections allowed (0 = unlimited)
 * @param {string} [props.descriptiveText] - Optional descriptive text displayed below the label
 * @returns {JSX.Element} The rendered multi-select component
 */
export default function MultiSelectBox({
    control,
    name,
    label,
    options = [],
    disabled = false,
    maximumSelectionLength = 0,
    descriptiveText
}) {
    const multiSelectRef = useRef();
    const selectId = useRef(`select-${v4()}`);

    const {
        field: { onChange, onBlur, value, ref },
        fieldState: { error }
    } = useController({
        name,
        control,
        defaultValue: ""
    });

    useEffect(() => {
        if (options && options.length > 0) {
            // Initialize GDK MultipleSelectBox
            multiSelectRef.current = new GdkMultipleSelectBox({
                content: `#${selectId.current}`,
                initialOptions: {
                    maximumSelectionLength: maximumSelectionLength
                }
            });

            // Set initial value if provided
            if (value?.length > 0) {
                $(`#${selectId.current}`).val(value).trigger("change");
            }

            // Handle change events
            $(`#${selectId.current}`).on('change', function () {
                const selectedValues = getMultiSelectValues(selectId.current);
                onChange(selectedValues);
            });

            // Handle blur events
            $(`#${selectId.current}`).on('blur', onBlur);
        }

        // Cleanup function
        return () => {
            if (multiSelectRef.current) {
                $(`#${selectId.current}`).off('change');
                $(`#${selectId.current}`).off('blur');
            }
        };
    }, [name, options, value, onChange, onBlur, maximumSelectionLength]);

    return (
        <div className={classes("form-field", error && "form-field--error")}>
            <label htmlFor={selectId.current} className="text">
                {label}
            </label>
            {descriptiveText && (
                <span className="form-descriptive-copy">{descriptiveText}</span>
            )}
            <div>
                <select multiple id={selectId.current} disabled={disabled}>
                    {options?.map(option => (
                        <option value={option.value} key={option.value}>
                            {option.name ?? option.label ?? option.text}
                        </option>
                    ))}
                </select>
                <input type="hidden" ref={ref} />
            </div>
            {error && (
                <span className="form-message">
                    {error.message}
                </span>
            )}
        </div>
    );
}
